// Adaptive render-distance cap (perf). Flying a fast aircraft on a weak client, no
// streaming budget can fill the disc between the plane's per-frame jumps — the
// honest fix is to pull the view in when the framerate can't sustain it. This is a
// dynamic CAP on top of the user's render-distance setting: it never exceeds what
// the player chose, shrinks toward a floor when fps sags, and grows back toward the
// setting when there's headroom. The setting itself is untouched — the options UI
// still shows the player's choice; only the streamed radius adapts.
//
// PURE + stepwise: one call = at most one step (±1 chunk), driven at a throttled
// cadence in the render loop, so the radius eases rather than snapping. A target
// band (FPS_LOW…FPS_HIGH) gives hysteresis so it settles instead of oscillating.
import { STREAM } from '../../sim/config';

/** The render-distance cap to use next adjustment tick.
 *  - `current` — last cap (chunks).
 *  - `fps`     — measured frames/second (0 before the first sample ⇒ hold).
 *  - `userMax` — the player's render-distance setting (hard ceiling). */
export function nextRenderCap(current: number, fps: number, userMax: number): number {
  const cap = Math.min(current, userMax); // a lowered setting clamps down immediately
  if (fps > 0 && fps < STREAM.FPS_LOW) return Math.max(STREAM.RENDER_MIN, cap - 1); // struggling → pull in
  if (fps >= STREAM.FPS_HIGH && cap < userMax) return cap + 1;                       // headroom → let out
  return cap;                                                                        // in the band → hold
}
